import React from 'react';
import { BarChart3, ChevronDown, PieChart, LayoutList } from 'lucide-react';
import type { DataSourceConfig } from '@/services/PagesServices/CustomReportBuilderService';
import { useReportBuilderState } from '../hooks/useReportBuilderState';
import type { ChartType, AggregateFunction } from '../constants';

interface VisualizationSectionProps {
  currentSource: DataSourceConfig | undefined;
  isLoading: boolean;
  onGenerateChart: () => void;
}

const VisualizationSection: React.FC<VisualizationSectionProps> = ({
  currentSource,
  isLoading,
  onGenerateChart,
}) => {
  const expanded = useReportBuilderState(s => s.expandedSections.visualization);
  const selectedDataSource = useReportBuilderState(s => s.selectedDataSource);
  const chartType = useReportBuilderState(s => s.chartType);
  const chartGroupByField = useReportBuilderState(s => s.chartGroupByField);
  const chartAggregateField = useReportBuilderState(s => s.chartAggregateField);
  const chartAggregateFunction = useReportBuilderState(s => s.chartAggregateFunction);
  const toggleSection = useReportBuilderState(s => s.toggleSection);
  const setChartType = useReportBuilderState(s => s.setChartType);
  const setChartGroupByField = useReportBuilderState(s => s.setChartGroupByField);
  const setChartAggregateField = useReportBuilderState(s => s.setChartAggregateField);
  const setChartAggregateFunction = useReportBuilderState(s => s.setChartAggregateFunction);

  return (
    <div className="crb-config-section">
      <div className="crb-config-header" onClick={() => toggleSection('visualization')}>
        <div className="crb-config-header-left">
          <BarChart3 size={18} />
          <span className="crb-config-header-title">Visualization</span>
        </div>
        <ChevronDown size={16} className={`crb-config-chevron ${expanded ? 'open' : ''}`} />
      </div>

      {expanded && currentSource && (
        <div className="crb-config-body">
          <div className="crb-sort-group">
            {/* Chart Type */}
            <div className="crb-sort-row">
              <span className="crb-sort-label">Chart Type</span>
              <div className="crb-chart-type-grid">
                <button
                  className={`crb-chart-type-btn ${chartType === 'bar' ? 'active' : ''}`}
                  onClick={() => setChartType('bar')}
                >
                  <BarChart3 size={16} /> Bar
                </button>
                <button
                  className={`crb-chart-type-btn ${chartType === 'horizontal' ? 'active' : ''}`}
                  onClick={() => setChartType('horizontal')}
                >
                  <LayoutList size={16} /> H-Bar
                </button>
                <button
                  className={`crb-chart-type-btn ${chartType === 'pie' ? 'active' : ''}`}
                  onClick={() => setChartType('pie')}
                >
                  <PieChart size={16} /> Pie
                </button>
              </div>
            </div>

            {/* Group By */}
            <div className="crb-sort-row">
              <span className="crb-sort-label">Group By <span style={{ color: '#ef4444' }}>*</span></span>
              <select
                className="crb-sort-select"
                value={chartGroupByField}
                onChange={(e) => setChartGroupByField(e.target.value)}
              >
                <option value="">Select field...</option>
                {currentSource.fields.map(f => (
                  <option key={f.key} value={f.key}>{f.label}</option>
                ))}
              </select>
            </div>

            {/* Measure Function */}
            <div className="crb-sort-row">
              <span className="crb-sort-label">Measure</span>
              <select
                className="crb-sort-select"
                value={chartAggregateFunction}
                onChange={(e) => setChartAggregateFunction(e.target.value as AggregateFunction)}
              >
                <option value="count">Count</option>
                <option value="sum">Sum</option>
                <option value="avg">Average</option>
                <option value="max">Maximum</option>
                <option value="min">Minimum</option>
              </select>
            </div>

            {/* Measure Field */}
            {chartAggregateFunction !== 'count' && (
              <div className="crb-sort-row">
                <span className="crb-sort-label">Measure Field</span>
                <select
                  className="crb-sort-select"
                  value={chartAggregateField}
                  onChange={(e) => setChartAggregateField(e.target.value)}
                >
                  <option value="">Select field...</option>
                  {currentSource.fields.map(f => (
                    <option key={f.key} value={f.key}>{f.label}</option>
                  ))}
                </select>
              </div>
            )}

            <button
              className="crb-run-btn"
              style={{ marginTop: '8px' }}
              onClick={onGenerateChart}
              disabled={!selectedDataSource || !chartGroupByField || isLoading}
            >
              <BarChart3 size={16} />
              {isLoading ? 'Generating...' : 'Generate Chart'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(VisualizationSection);
